import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, Drawer } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getProductRuptures } from '../../services/rapportService';
import {
  NdoePage, NdoePageHeader, NdoePeriodPicker, NdoeActionButton,
  NdoeResponsiveTable, NdoeDataCard, NdoeStatusBadge, NdoeImage,
  NdoeStats, NdoeStatCard, NdoeError,
  NdoeArticleLink, useFicheArticle,
} from '../../components/ndoe';
import { toCsv, downloadCsv } from './exportCsv';
import useRefLists from './useRefLists';
import ReportFilters from './ReportFilters';
import { money, days, rate, dateTxt, ago, CONFIANCE_COURT } from './format';
import './rapports.css';

const recoBadge = (row) => (
  <NdoeStatusBadge kind="custom" tone={row.recommandation_tone} label={row.recommandation_label} />
);
const confianceBadge = (row) => (
  <NdoeStatusBadge kind="custom" tone={row.confiance_tone} label={CONFIANCE_COURT[row.confiance] || row.confiance_label} />
);

const SORTS = [
  { value: 'cadence', label: 'Demande avant rupture' },
  { value: 'quantite', label: 'Quantité vendue' },
  { value: 'ca', label: "Chiffre d'affaires" },
  { value: 'recent', label: 'Rupture la plus récente' },
  { value: 'pointures', label: 'Pointures concernées' },
];
const RECOS = [
  { value: '', label: 'Toutes les décisions' },
  { value: 'restock', label: 'À recommander' },
  { value: 'watch', label: 'À surveiller' },
  { value: 'avoid', label: 'Ne pas recommander' },
];

const EMPTY_FILTERS = { marque: '', categorie: '', matiere: '', couleur: '', pointure: '', recommandation: '' };

export default function RupturesInteressantes() {
  const user = useSelector((s) => s.user?.currentUser);
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';

  const [period, setPeriod] = useState({ period: 30 });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('cadence');
  const [page, setPage] = useState(1);

  const [payload, setPayload] = useState({ meta: {}, summary: {}, data: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [detail, setDetail] = useState(null);
  const { openFiche, ficheElement } = useFicheArticle();

  const refs = useRefLists(isAdmin);

  useEffect(() => {
    const timer = setTimeout(() => { setSearch(q.trim()); setPage(1); }, 400);
    return () => clearTimeout(timer);
  }, [q]);

  const apiParams = useMemo(() => ({
    period: period.period === 'custom' ? undefined : period.period,
    start_date: period.period === 'custom' ? period.start_date : undefined,
    end_date: period.period === 'custom' ? period.end_date : undefined,
    ...filters, q: search, sort,
  }), [period, filters, search, sort]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getProductRuptures({ ...apiParams, page, page_size: 20 });
      setPayload(data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Impossible de charger le rapport des ruptures.');
    } finally { setLoading(false); }
  }, [apiParams, page]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);
  useEffect(() => { setPage(1); }, [period, filters, sort]);

  const setFilter = (key) => (value) => setFilters((f) => ({ ...f, [key]: value ?? '' }));
  const activeCount = Object.values(filters).filter(Boolean).length + (search ? 1 : 0);

  const handleExport = async () => {
    setExporting(true);
    try {
      const all = [];
      let current = 1;
      let total = Infinity;
      while (all.length < total && current <= 25) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await getProductRuptures({ ...apiParams, page: current, page_size: 60 });
        total = data.meta?.total ?? all.length;
        all.push(...(data.data || []));
        if (!data.data?.length) break;
        current += 1;
      }
      const columns = [
        { title: 'Produit', value: (r) => r.nom_produit },
        { title: 'Marque', value: (r) => r.marque },
        { title: 'Catégorie', value: (r) => r.categorie },
        { title: 'Matière', value: (r) => r.matiere },
        { title: 'Pointures en rupture', value: (r) => r.pointures_rupture.map((p) => `P${p.taille}:${p.vendu}`).join(' ') },
        { title: 'Nb pointures en rupture', value: (r) => r.nb_pointures_rupture },
        { title: 'Quantité vendue (période)', value: (r) => r.quantite_vendue },
        { title: "Chiffre d'affaires", value: (r) => r.chiffre_affaires },
        { title: 'Paires vendues 30 j avant rupture', value: (r) => r.quantite_avant_rupture },
        { title: 'Demande avant rupture (paires/mois)', value: (r) => r.cadence_mensuelle },
        { title: 'Ventes manquées (est.)', value: (r) => r.ventes_manquees || '' },
        { title: 'Écoulement de fond (paires/mois)', value: (r) => r.cadence_periode },
        { title: 'Date rupture estimée', value: (r) => dateTxt(r.date_rupture_estimee) },
        { title: 'Jours depuis rupture', value: (r) => r.jours_depuis_rupture ?? '' },
        { title: 'Dernière entrée', value: (r) => dateTxt(r.derniere_entree) },
        { title: 'Délai FIFO moyen (j)', value: (r) => r.delai_fifo_jours ?? '' },
        { title: 'Stock autres pointures', value: (r) => r.stock_autres_pointures },
        { title: 'Confiance donnée', value: (r) => r.confiance_label },
        { title: 'Recommandation', value: (r) => r.recommandation_label },
        { title: 'Raison', value: (r) => r.raison },
      ];
      const { start_date: from, end_date: to } = payload.meta || {};
      downloadCsv(`ruptures-interessantes_${from || ''}_${to || ''}.csv`, toCsv(columns, all));
      toast.success(`${all.length} produit(s) exporté(s).`);
    } catch (e) {
      toast.error("L'export a échoué.");
    } finally { setExporting(false); }
  };

  if (!isAdmin) {
    return (
      <NdoePage>
        <NdoeError title="Accès réservé" text="Le module Rapports est réservé à l'administrateur." />
      </NdoePage>
    );
  }

  const { summary = {}, meta = {}, data = [] } = payload;

  const columns = [
    {
      title: 'Photo', dataIndex: 'img', width: 76,
      render: (img, r) => <NdoeImage src={img} alt={r.nom_produit} size="md" preview={false} />,
    },
    {
      title: 'Produit', dataIndex: 'nom_produit',
      render: (nom, r) => (
        <div className="pp-produit">
          <b>{nom || '—'}</b>
          <small>{[r.marque, r.categorie, r.matiere].filter(Boolean).join(' · ') || '—'}</small>
          <div className="pp-pointures">
            {r.pointures_rupture.map((p) => (
              <span className="pp-pt pp-pt--out" key={p.taille}>P{p.taille}<i>{p.vendu}</i></span>
            ))}
          </div>
          {r.deconseille && <NdoeStatusBadge kind="custom" tone="danger" label="Déconseillé" />}
        </div>
      ),
    },
    { title: 'Vendu', dataIndex: 'quantite_vendue', align: 'right', width: 82, render: (v) => <span className="num">{v}</span> },
    {
      title: 'Demande avant rupture', dataIndex: 'cadence_mensuelle', align: 'right', width: 150,
      render: (v, r) => (
        <div className="rp-demande">
          <b className="num">{rate(v)}</b>
          <small>{r.quantite_avant_rupture} paire(s) sur 30 j</small>
        </div>
      ),
    },
    {
      title: 'Rupture', dataIndex: 'jours_depuis_rupture', width: 148,
      render: (v, r) => (
        <div className="rp-rupture">
          <b>{ago(v)}</b>
          <small>≈ {dateTxt(r.date_rupture_estimee)}</small>
        </div>
      ),
    },
    { title: 'Pointures', dataIndex: 'nb_pointures_rupture', align: 'right', width: 96, render: (v) => <span className="num">{v}</span> },
    {
      title: 'Autres pointures', dataIndex: 'stock_autres_pointures', align: 'right', width: 128,
      render: (v) => <span className="num">{v > 0 ? `${v} en stock` : 'aucune'}</span>,
    },
    { title: 'Délai FIFO', dataIndex: 'delai_fifo_jours', align: 'right', width: 100, render: (v) => <span className="num">{days(v)}</span> },
    { title: 'Décision', dataIndex: 'recommandation', width: 150, render: (_, r) => recoBadge(r) },
    { title: 'Confiance', dataIndex: 'confiance', width: 126, render: (_, r) => confianceBadge(r) },
  ];

  const renderCard = (r) => (
    <NdoeDataCard
      key={r.id_produit}
      media={<NdoeImage src={r.img} alt={r.nom_produit} size="lg" preview={false} />}
      title={r.nom_produit || '—'}
      subtitle={[r.marque, r.categorie].filter(Boolean).join(' · ')}
      badge={recoBadge(r)}
      metrics={[
        { label: 'Vendu', value: r.quantite_vendue },
        { label: 'Demande avant rupture', value: rate(r.cadence_mensuelle) },
        { label: 'Rupture', value: ago(r.jours_depuis_rupture) },
        { label: 'Pointures', value: r.nb_pointures_rupture },
      ]}
      onClick={() => setDetail(r)}
    />
  );

  const KpiCard = ({ label, value, gold, onClick }) => (
    onClick ? (
      <div className="pp-kpi-click" role="button" tabIndex={0} onClick={onClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}>
        <NdoeStatCard label={label} value={value} gold={gold} />
      </div>
    ) : <NdoeStatCard label={label} value={value} gold={gold} />
  );

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Rapports"
        title="Ruptures intéressantes"
        subtitle="Ce qui se vendait bien et vient de tomber à zéro — les réassorts à passer en priorité."
        actions={
          <NdoeActionButton variant="secondary" icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>
            Exporter CSV
          </NdoeActionButton>
        }
      />

      <div className="pp-period">
        <NdoePeriodPicker value={period} onChange={setPeriod} />
        {meta.start_date && (
          <span className="pp-range">du {dateTxt(meta.start_date)} au {dateTxt(meta.end_date)}</span>
        )}
      </div>

      <NdoeStats className="pp-stats">
        <KpiCard label="Produits en rupture" value={summary.produits ?? 0} />
        <KpiCard label="Pointures concernées" value={summary.pointures_rupture ?? 0} />
        <KpiCard label="Paires vendues" value={summary.paires_vendues ?? 0} />
        <KpiCard label="À recommander" value={summary.a_recommander ?? 0} gold
          onClick={() => setFilter('recommandation')('restock')} />
        <KpiCard label="À surveiller" value={summary.a_surveiller ?? 0}
          onClick={() => setFilter('recommandation')('watch')} />
        <KpiCard label="Ruptures ≤ 7 j" value={summary.ruptures_7j ?? 0} />
      </NdoeStats>

      <p className="pp-note rp-note">
        <NdoeStatusBadge kind="custom" tone="info" label="Lecture" />
        {' '}Seules les pointures qui <b>vendaient</b> puis sont tombées à zéro apparaissent ici&nbsp;: une pointure
        à zéro qui ne se vendait pas n'est pas une opportunité de réassort. La date de rupture est <b>estimée</b>
        {' '}à partir de la dernière vente.
      </p>

      <ReportFilters
        q={q} setQ={setQ}
        filters={filters} setFilter={setFilter}
        refs={refs}
        sort={sort} setSort={setSort} sorts={SORTS}
        activeCount={activeCount}
      >
        <div className="ndoe-filter-bar__field">
          <Select placeholder="Décision" style={{ minWidth: 185 }} options={RECOS}
            value={filters.recommandation} onChange={setFilter('recommandation')} />
        </div>
      </ReportFilters>

      {error && !loading ? (
        <NdoeError text={error} onRetry={load} />
      ) : (
        <NdoeResponsiveTable
          className="pp-table"
          data={data}
          columns={columns}
          renderCard={renderCard}
          rowKey="id_produit"
          loading={loading}
          error={error}
          onRetry={load}
          scroll={{ x: 1200 }}
          onRow={(record) => ({ onClick: () => setDetail(record), style: { cursor: 'pointer' } })}
          pagination={{ current: meta.page || 1, pageSize: meta.page_size || 20, total: meta.total || 0, showSizeChanger: false }}
          onChange={(pagination) => setPage(pagination?.current || 1)}
          empty={{
            icon: '✅',
            title: 'Aucune rupture intéressante sur cette période',
            text: 'Rien qui se vendait bien n\'est tombé à zéro. Élargis la période pour remonter plus loin.',
          }}
        />
      )}

      <Drawer
        open={!!detail}
        onClose={() => setDetail(null)}
        width={560}
        title={detail ? <NdoeArticleLink onClick={() => openFiche(detail.id_produit)}>{detail.nom_produit}</NdoeArticleLink> : ('' || 'Détail rupture')}
        className="pp-drawer"
      >
        {detail && (
          <div className="pp-detail">
            <div className="pp-detail__head">
              <NdoeImage src={detail.img} alt={detail.nom_produit} size="hero" />
              <div className="pp-detail__meta">
                <b>{[detail.marque, detail.categorie].filter(Boolean).join(' · ') || '—'}</b>
                <small>{detail.matiere || 'Matière non renseignée'}</small>
                <small>{(detail.couleurs || []).join(', ') || 'Couleur non renseignée'}</small>
                <div className="pp-detail__badges">
                  {recoBadge(detail)}
                  {confianceBadge(detail)}
                  {detail.deconseille && <NdoeStatusBadge kind="custom" tone="danger" label="Déconseillé" />}
                </div>
              </div>
            </div>

            <div className="rp-raison">
              <span className="rp-raison__label">Pourquoi cette décision</span>
              <p>{detail.raison}</p>
            </div>

            <NdoeStats>
              <NdoeStatCard label="Demande avant rupture" value={rate(detail.cadence_mensuelle)} gold />
              <NdoeStatCard label="Vendu sur la période" value={detail.quantite_vendue} />
              <NdoeStatCard label="Chiffre d'affaires" value={money(detail.chiffre_affaires)} />
              <NdoeStatCard label="Rupture" value={ago(detail.jours_depuis_rupture)} />
              <NdoeStatCard label="Pointures à zéro" value={detail.nb_pointures_rupture} />
              <NdoeStatCard label="Autres pointures" value={detail.stock_autres_pointures} />
              <NdoeStatCard label="Ventes manquées (est.)" value={detail.ventes_manquees > 0 ? `≈ ${detail.ventes_manquees}` : '—'} />
            </NdoeStats>
            {detail.ventes_manquees > 0 && (
              <p className="pp-detail__warn">
                Pendant que ces pointures étaient à zéro, <b>d'autres pointures du modèle vendaient encore</b>&nbsp;:
                ≈&nbsp;<b>{detail.ventes_manquees}</b> vente(s) sans doute manquée(s) (estimation prudente, au rythme
                d'avant rupture, bornée à 2× le vendu). Quand rien ne vendait après la rupture, on ne compte rien.
              </p>
            )}
            <p className="pp-detail__warn">
              La <b>demande avant rupture</b> compte les paires vendues dans les 30 jours qui ont précédé
              la rupture&nbsp;: elle ne dépend pas de la période choisie. L'écoulement de fond sur la période
              est de {rate(detail.cadence_periode)}, donné à titre indicatif.
            </p>

            <h4 className="pp-detail__title">Pointures tombées à zéro</h4>
            <div className="pp-detail__pt rp-pt">
              <div className="pp-detail__pt-row rp-pt-row pp-detail__pt-row--head">
                <span>Pointure</span><span>Vendu</span><span>Dernière vente</span><span>Dernière entrée</span>
              </div>
              {detail.pointures_rupture.map((p) => (
                <div className="pp-detail__pt-row rp-pt-row" key={p.taille}>
                  <span>P{p.taille}</span>
                  <span className="num">{p.vendu}</span>
                  <span className="num">{dateTxt(p.derniere_vente)}</span>
                  <span className="num">{p.derniere_entree ? dateTxt(p.derniere_entree) : '—'}</span>
                </div>
              ))}
            </div>

            <h4 className="pp-detail__title">Rotation (FIFO entrée → vente)</h4>
            {detail.delai_fifo_jours === null ? (
              <p className="pp-detail__warn">
                Aucune réception tracée&nbsp;: le délai FIFO n'est pas calculé. La décision ci-dessus s'appuie sur la
                cadence de vente, qui reste fiable.
              </p>
            ) : (
              <p>
                Délai moyen <b>{days(detail.delai_fifo_jours)}</b>
                {detail.delai_fifo_min !== null && <> · min {days(detail.delai_fifo_min)} · max {days(detail.delai_fifo_max)}</>}
              </p>
            )}

            <h4 className="pp-detail__title">Chronologie</h4>
            <ul className="pp-detail__dates">
              <li><span>Rupture estimée</span><b>{dateTxt(detail.date_rupture_estimee)}</b></li>
              <li><span>Dernière entrée en stock</span><b>{dateTxt(detail.derniere_entree)}</b></li>
            </ul>

            {detail.deconseille && detail.motif_deconseille && (
              <p className="pp-detail__motif">Motif du déconseil&nbsp;: {detail.motif_deconseille}</p>
            )}
          </div>
        )}
      </Drawer>
      {ficheElement}
    </NdoePage>
  );
}
